<template>
  <b-container fluid>
    <b-row class='mb-2 my-1'>
      <b-col md=3>
        <h1 class='my-3'>Pack & Ship</h1>
      </b-col>
      <b-col md=9 lg=6>
        <b-card header="Order Lookup">
          <section v-if=loading class='card-loader'>
            <i class='fas fa-spinner fa-spin fa-3x'></i>
          </section>
          <b-row class='mb-1'>
            <b-col>
              <b-form-input v-model='orderId' type=text placeholder='Order ID' @keydown.enter="loadSingleOrder" />
            </b-col>
            <b-col cols=* class='mx-1'>
              <b-btn variant=success block @click='loadSingleOrder'>Load</b-btn>
            </b-col>
            <b-col cols=* class='mx-1'>
              <b-btn variant=info block :disabled='orders.length < 1' @click='fetchOrder'>Combine</b-btn>
            </b-col>
          </b-row>
        </b-card>
      </b-col>
      <b-col lg=3>
        <b-card header=Orders class='text-center'>
          <b-badge v-for="order in orders" :key=order class='m-1' @click="removeOrder(order)">{{order}}</b-badge>
        </b-card>
      </b-col>
    </b-row>

    <b-row class='mb-2'>
      <b-col md=6 xl=4>
        <b-card header='Delivery Address'>
          <section v-if=loading class='card-loader'>
            <i class='fas fa-spinner fa-spin fa-3x'></i>
          </section>
          <b-form-input class="my-1" required v-model='deliveryAddress.address1' type="text" placeholder="Street Address" />
          <b-form-row class="my-1">
            <b-col>
              <b-form-input required v-model="deliveryAddress.city" type="text" placeholder="City" />
            </b-col>
            <b-col>
              <b-form-input required v-model='deliveryAddress.postal_code' type=text placeholder='Delivery ZIP code' />
            </b-col>
          </b-form-row class="my-1">
          <b-form-row>
            <b-col>
              <b-form-select required v-model="deliveryAddress.province" :options="niceStates" type="text" placeholder="Region" />
            </b-col>
            <b-col>
              <b-form-select required v-if=countries :options=niceCountries v-model=deliveryAddress.country />
            </b-col>
          </b-form-row>
        </b-card>
      </b-col>
      <b-col>
        <b-tabs card v-model="packingMode" style="border:1px solid rgba(0,0,0,.125);">
          <section v-if="loading" class="card-loader">
            <!-- eslint-disable-next-line vue/html-self-closing -->
            <i class="fas fa-spinner fa-spin fa-3x"></i>
          </section>
          <b-tab title="Item Mode">
            <section style="overflow-y: scroll; height: 16rem;">
              <b-table :fields="itemColumns" :items="items">
                <template slot='sku' slot-scope='row'>
                  {{row.item.sku}}
                  <b-btn variant=link class='m-0 p-0' v-if="row.item.blank" v-b-popover.hover="'Blank items are not shippable using this tool!'">
                    <i class="fas fa-exclamation-circle" style="color:red;"></i>
                  </b-btn>
                </template>
                <template slot="actions" slot-scope="row">
                  <b-btn variant="danger" @click="removeItem(row.item)">
                    <!-- eslint-disable-next-line vue/html-self-closing -->
                    <i class="fas fa-trash-alt"></i>
                  </b-btn>
                </template>
                <template slot="qty" slot-scope="row">
                  <b-form-input v-model="row.item.qty" type="number" style="max-width:75px;" />
                </template>
                <template slot="price" slot-scope="row">
                  <b-input-group>
                    <b-input-group-prepend is-text>
                      $
                    </b-input-group-prepend>
                    <b-form-input v-model="row.item.price" type="number" style="max-width:100px;" />
                  </b-input-group>
                </template>
              </b-table>
              <b-btn block class="my-2" variant="success" @click="showItemModal">
                +
              </b-btn>
            </section>
          </b-tab>
          <b-tab title="Box Mode">
            <section style="overflow-y:scroll; height: 16rem;">
              <b-table :fields="boxColumns" :items="boxes">
                <template slot="actions" slot-scope="row">
                  <b-btn variant="danger" @click="removeBox(row.item)">
                    <!-- eslint-disable-next-line vue/html-self-closing -->
                    <i class="fas fa-trash-alt"></i>
                  </b-btn>
                </template>
                <template slot="length" slot-scope="row">
                  <b-input-group>
                    <b-form-input v-model="row.item.length" style="max-width:100px;" />
                    <b-input-group-append is-text>
                      inches
                    </b-input-group-append>
                  </b-input-group>
                </template>
                <template slot="width" slot-scope="row">
                  <b-input-group>
                    <b-form-input v-model="row.item.width" style="max-width:100px;" />
                    <b-input-group-append is-text>
                      inches
                    </b-input-group-append>
                  </b-input-group>
                </template>
                <template slot="height" slot-scope="row">
                  <b-input-group>
                    <b-form-input v-model="row.item.height" style="max-width:100px;" />
                    <b-input-group-append is-text>
                      inches
                    </b-input-group-append>
                  </b-input-group>
                </template>
                <template slot="weight" slot-scope="row">
                  <b-input-group>
                    <b-form-input v-model="row.item.weight" style="max-width:100px;" />
                    <b-input-group-append is-text>
                      lbs
                    </b-input-group-append>
                  </b-input-group>
                </template>
                <template slot="value" slot-scope="row">
                  <b-input-group>
                    <b-input-group-prepend is-text>
                      $
                    </b-input-group-prepend>
                    <b-form-input v-model="row.item.value" type="number" style="max-width:100px;" />
                  </b-input-group>
                </template>
              </b-table>
              <b-btn block class="my-2" variant="success" @click="addBox">
                +
              </b-btn>
            </section>
          </b-tab>
        </b-tabs>
      </b-col>
    </b-row>
    <b-row class="mb-2">
      <b-col>
        <b-alert :show="hasBlankItems" variant="warning" class='mb-0'>
          Please remove the blank items from above before you ship this order!
        </b-alert>
        <b-btn block variant="success" @click="getShipping()" :disabled="loading || hasBlankItems">
          Calculate >>>
        </b-btn>
      </b-col>
    </b-row>
    <section v-if="!loading && shipments.length > 0">
      <b-row>
        <b-col>
          <b-card header="Packages" :bg-variant="dirty ? 'warning' : 'default'">
            <p class="text-center">
              Shipping {{ itemCount }} individual items in {{ shipments.length }} packages
              ({{ shipmentWeight }} lbs)
            </p>
            <b-form-row>
              <b-col>
                <b-form-row class="text-center font-weight-bold">
                  <b-col cols=1 />
                  <b-col>Dimensions</b-col>
                  <b-col cols=3>Weight (lbs)</b-col>
                  <b-col cols=1 />
                </b-form-row>
                <section v-for="shipment,ix in shipments" :key="'parcel-' + ix">
                  <b-form-row class="mb-4 align-items-center">
                    <b-col cols=1 :class="'text-center py-1'+parcelVariant(shipment)">
                      <span class="fa-layers fa-fw fa-2x">
                        <i class="fas fa-box"></i>
                        <span class="fa-layers-counter fa-layers-bottom-right bg-danger" style="font-size: 1.3em">
                          {{ ix+1 }}
                        </span>
                      </span>
                    </b-col>
                    <b-col>
                      <b-form-row>
                        <b-col><b-form-input type=number v-model="shipment.parcel.length" disabled /></b-col>
                        <b-col><b-form-input type=number v-model="shipment.parcel.width" disabled /></b-col>
                        <b-col><b-form-input type=number v-model="shipment.parcel.height" disabled /></b-col>
                        <b-col><b-form-input type=number v-model="shipment.parcel.weight" disabled /></b-col>
                        <b-col cols=1><b-btn @click="selectShipment(ix)" size="sm" variant="link"><i class="fas fa-question-circle"></i></b-btn></b-col>
                      </b-form-row>
                      <b-form-row>
                        <b-col class="small pt-1">
                          <b-row>
                            <b-col v-for="item, iix in niceContents(shipment)" :key="`parcel-${ix}-${iix}`" cols=3>
                              {{ item }}
                            </b-col>
                          </b-row>
                        </b-col>
                      </b-form-row>
                    </b-col>
                  </b-form-row>
                </section>
              </b-col>
              <b-col cols="*" style="margin-bottom:3rem; margin-top:1.5rem;">
                <b-btn variant="secondary" size="sm" block @click="loadBoxMode" class="px-3" style="height: 100%">
                  Copy to Box Mode >>>
                </b-btn>
              </b-col>
            </b-form-row>
          </b-card>
        </b-col>
        <b-col md=6>
          <b-card header=Rates :bg-variant="dirty ? 'warning' : 'default'">
            <b-table :fields="rateColumns" :items="rates">
              <template slot="service_name" slot-scope="data">
                <span :style="rateColor(data.item.service_name)">{{data.item.service_name}}</span>
                <div style="line-height:80%" v-if=data.item.info_min_delivery_days>
                  <span class='small'>{{data.item.info_min_delivery_days}} day ship</span>
                  <em v-if="data.item.info_min_delivery_date" class="small">
                    <br/>by <NiceDate :value=data.item.info_min_delivery_date isUtc />
                  </em>
                </div>
              </template>
              <template slot="web_rate" slot-scope="data">
                {{ data.item.total_price / 100 | formatDollars }} {{ data.item.currency }}
                <b-btn variant="link" class="m-0 p-0" v-b-popover.hover="'$' + (data.item.retail_rate - data.item.total_price) / 100 + ' less than retail rate, $' + (data.item.total_price - data.item.discounted_rate) / 100 + ' more than discounted rate'">
                  <i class="fas fa-info-circle"></i>
                </b-btn>
              </template>
              <template slot="discounted_rate" slot-scope="data">
                {{ data.item.discounted_rate / 100 | formatDollars }} {{ data.item.currency }}
              </template>
              <template slot="retail_rate" slot-scope="data">
                {{ data.item.retail_rate / 100 | formatDollars }} {{ data.item.currency }}
              </template>
            </b-table>
          </b-card>
        </b-col>
      </b-row>
    </section>
    <section v-if="this.shipments.length > 0">
      <b-row class="m-1 mt-2">
        <b-btn block v-b-toggle.logs-collapse variant="secondary">Show Logs</b-btn>
      </b-row>
      <b-collapse id="logs-collapse">
        <b-row v-for="log,ix in logs" :key="`log-${ix}`" class="mx-4" style="border:1px;border-color:grey;border-style:dotted;">
          <b-col>
            {{ log }}
          </b-col>
        </b-row>
        <hr/>
        <section v-for="log,ix in log_pairs" :key="`logpair-${ix}`" class="mx-4"style="border:1px;border-color:grey;border-style:dotted;">
          <b-row>
            <b-col>
              <pre>{{ log.request }}</pre>
            </b-col>
            <b-col>
              <pre>{{ log.response }}</pre>
            </b-col>
          </b-row>
      </section>
      </b-collapse>
    </section>

    <b-modal ref=itemModalRef centered title="Add Item" size=lg>
      <b-row>
        <b-col>
          <b-form-input required v-model=sku placeholder=SKU type=text @input="debouncedSearchSku" />
        </b-col>
      </b-row>
      <b-row class='mt-3'>
        <b-col>
          <b-table :fields=modalFields :items=modalItems>
            <template slot=image slot-scope=row>
              <img :src=row.item.image />
            </template>
            <template slot='actions' slot-scope=row>
              <b-btn variant=success @click="addItem(row.item.sku)">+</b-btn>
            </template>
          </b-table>
        </b-col>
      </b-row>
    </b-modal>

    <b-modal ref="shipmentInfoRef" centered title="Shipment Info" size="xl">
      <div v-if="selectedShipment">
        <b-form-row class="justify-content-center text-center mt-2">
          <strong class="mr-1">Shipment:</strong> {{ selectedShipment.id }}
        </b-form-row>
        <b-form-row class="justify-content-center text-center mt-2">
          <strong class="mr-1">Parcel: </strong> {{ selectedShipment.parcel.id }}
        </b-form-row>
        <b-form-row class="justify-content-center text-center mt-2">
          <b-col cols=1 />
          <b-col>
            {{ selectedShipment.parcel.length }}" x
            {{ selectedShipment.parcel.width }}" x
            {{ selectedShipment.parcel.height }}" @
            {{ selectedShipment.parcel.weight }} lbs
          </b-col>
        </b-form-row>
        <b-row class="justify-content-center text-center mt-2">
          <h3>Rates:</h3>
        </b-row>
        <b-row v-for="rate in selectedShipment.rates" :key="`rate-${rate.carrier}-${rate.service}`" class="justify-content-center text-center">
          <h5 class="mr-1">{{ rate.carrier }} / {{ rate.service }}:</h5>
          retail: {{ !!rate.retail_rate ? rate.retail_rate : !!rate.list_rate ? rate.list_rate : !!rate.rate ? rate.rate : 0 | formatDollars }}
          AM: {{ !!rate.rate ? rate.rate : 0 | formatDollars }}
        </b-row>
        <b-row class="justify-content-center text-center mt-2">
          <h3>Items</h3>
        </b-row>
        <b-row v-for="item in selectedShipment.parcel.items" :key="`item-${item.id}`" class="justify-content-center text-center">
          <h5 class="mr-1">{{ item.sku }}:</h5>
          <span v-if="item.special_method" style="color:orangered;">
            {{ item.special_method }} - {{ item.special }}
          </span>
          <span v-else>
            {{ item.length }}" x {{ item.width }}" x {{  item.height }}" @ {{  item.weight }}lbs
          </span>
        </b-row>
      </div>
    </b-modal>
  </b-container>
</template>

<script>
import axios from 'axios';
import { mapActions, mapGetters } from 'vuex';
import mergeService from '../../services/orderMerge';
import NiceDate from '../utility/NiceDate.vue';

const BASE_API = process.env.VUE_API_ROOT

export default {
  data() {
    return {
      dirty: false,
      loading: false,
      orders: [],

      itemModalShowing: false,
      sku: '',
      timer: undefined,
      modalFields: ['sku', 'title', { key: 'actions', label: '' }],
      modalItems: [],

      orderId: '',
      deliveryAddress: {
        address1: '1900 East North Street',
        city: 'Kokomo',
        province: 'IN',
        country: 'US',
        postal_code: '46902',
      },

      packingMode: 0,

      itemColumns: [
        { key: 'actions', label: '' },
        { key: 'sku', label: 'SKU' },
        { key: 'description' },
        { key: 'qty' },
        { key: 'price' }
      ],
      items: [],

      boxColumns: [
        { key: 'actions', label: '' },
        { key: 'length' },
        { key: 'width' },
        { key: 'height' },
        { key: 'weight' },
        { key: 'value', label: 'Customs Value' },
      ],
      boxes: [],

      selectedShipmentIx: undefined,
      shipments: [],

      rateColumns: [
        'service_name',
        // 'description',
        'web_rate',
        'discounted_rate',
        'retail_rate',
        // 'min_delivery_days',
      ],
      rates: [],

      logs: [],
      log_pairs: []
    }
  },
  components: {
    NiceDate
  },
  computed: {
    ...mapGetters({
      countries: 'countries',
    }),
    niceStates() {
      if (this.countries && !!this.deliveryAddress.country) {
        const currentCountry = this.countries[this.deliveryAddress.country];
        if (!currentCountry) {
          return {}
        }
        return currentCountry.states
      }
      return {};
    },
    niceCountries() {
      if (this.countries) {
        return Object.keys(this.countries).map((abbr) => {
          return { text: `${abbr} | ${this.countries[abbr].name}`, value: abbr }
        })
      }
      return { }
    },
    hasBlankItems() {
      return this.packingMode === 0 && this.items.some(i => i.blank);
    },
    selectedShipment() {
      return this.shipments[this.selectedShipmentIx]
    },
    itemCount() {
      if (this.packingMode === 1) {
        return '???'
      }
      return this.items.map(i => i.qty).reduce((memo,i) => memo += Number(i), 0);
    },
    shipmentWeight() {
      return this.shipments.map(i => i.parcel.weight).reduce((memo,i) => memo += Number(i), 0);
    },
  },
  watch: {
    packingMode() {
      this.dirty = true
    },
  },
  methods: {
    ...mapActions({
      getCountries: 'getCountries',
    }),
    loadSingleOrder() {
      this.orders = []
      this.items = []
      this.fetchOrder()
    },
    fetchOrder() {
      this.loading = true
      this.dirty = true
      this.packingMode = 0;
      this.orderId = this.orderId.toUpperCase()
      if (!this.orders.includes(this.orderId)) {
        this.orders.push(this.orderId)
      }
      this.shipments = []
      axios.get(`${BASE_API}/bridge/${this.orderId}`).then(async (response) => {
        const order = response.data.merged;

        this.deliveryAddress.country = mergeService.mergeField(order.shippingAddress.countryCode)
        this.deliveryAddress.address1 = mergeService.mergeField(order.shippingAddress.street1)
        this.deliveryAddress.city = mergeService.mergeField(order.shippingAddress.city)
        this.deliveryAddress.province = mergeService.mergeField(order.shippingAddress.regionCode)
        this.deliveryAddress.postal_code = mergeService.mergeField(order.shippingAddress.zip)

        order.items.filter(i => mergeService.mergeField(i.requiresShipping)).forEach(i => {
          let variantId = Number(mergeService.mergeField(i.variantId).split('/')[4])
          this.items.push({
            order: order.num.toUpperCase(),
            id: variantId,
            blank: !variantId,
            sku: mergeService.mergeField(i.sku),
            description: mergeService.mergeField(i.description),
            qty: mergeService.mergeField(i.qty),
            price: mergeService.mergeField(i.unitPrice).toFixed(2)
          })
        })
      }).catch((error) => {
        console.error(error)
      }).finally(() => {
        this.loading = false
      })
    },
    showItemModal() {
      this.$refs.itemModalRef.show()
    },
    hideItemModal() {
      this.$refs.itemModalRef.hide()
    },
    debouncedSearchSku() {
      if (this.timer) {
        clearTimeout(this.timer)
      }
      this.timer = setTimeout(this.searchSku, 500)
    },
    searchSku() {
      this.modalItems.splice(0, this.modalItems.length)
      axios.get(`${BASE_API}/shopify/product/search/${this.sku}`).then((response) => {
        response.data.forEach((item) => {
          this.modalItems.push({
            name: item.title,
            sku: item.sku,
          })
        })
      }).catch((error) => {
        console.error(error)
      })
    },
    async addItem(sku, order = undefined, qty = 1) {
      this.dirty = true
      return axios.get(`${BASE_API}/shopify/product/search/${sku}`).then((r) => {
        r.data.forEach((item) => {
          if (item.sku !== sku || !item.shippable) return;
          const id = Number(item.id.split('/')[4])

          const existingItem = this.items.find(i => i.id === id && i.order === order && Math.abs(i.price - Number(item.price)) < 0.01)
          if (existingItem) {
            existingItem.qty += 1;
          } else {
            this.items.push({
              order,
              id,
              blank: !id,
              sku,
              description: item.title,
              qty,
              price: Number(item.price).toFixed(2)
            })
          }
        })
      })
    },
    removeItem(item) {
      this.dirty = true
      this.items.splice(this.items.indexOf(item), 1)
    },
    removeOrder(order) {
      this.dirty = true
      this.items = this.items.filter(item => item.order !== order)
      this.orders.splice(this.orders.indexOf(order))
    },
    addBox() {
      this.dirty = true
      this.boxes.push({ length: 11, width: 11, height: 11, weight: 5, value: 1 })
    },
    removeBox(box) {
      this.dirty = true
      this.boxes.splice(this.boxes.indexOf(box), 1)
    },
    toggleMode() {
      this.dirty = true;
      if (this.packingMode === 0) {
        this.packingMode = 1
      } else {
        this.packingMode = 0
      }
    },
    getShipping() {
      this.loading = true;
      this.log_pairs = []
      this.logs = []
      this.rates = []
      this.shipments = []
      const payload = { destination: this.deliveryAddress }

      if (this.packingMode === 0) { // item mode
        payload.items = this.items.map(i => ({
          sku: i.sku,
          variant_id: i.id,
          quantity: parseInt(i.qty),
          requires_shipping: true,
          price: parseInt(i.price * 100, 10)
        }))
      } else if (this.packingMode === 1) { // box mode
        this.boxes.forEach((box) => {
          box.length = Number(box.length);
          box.width = Number(box.width);
          box.height = Number(box.height);
          box.weight = Number(box.weight);
          box.value = Math.round(Number(box.value));
        })
        payload.boxes = this.boxes;
        payload.items = []
      }

      axios({
        method: 'POST',
        headers: { 'Content-Type': 'text/plain' },
        url: `${BASE_API}/aws/lambda/shipping-server/rate`,
        data: payload,
      }).then((response) => {
        this.rates = response.data.rates.sort((a,b) => a.total_price - b.total_price)
        this.shipments = response.data.shipments.filter(x => !x.saturday_delivery)

        let requests = [];
        for (let ix = 0; ix < response.data.logs.length; ix += 1) {
          if (response.data.logs[ix].startsWith('!!!')) {
            const splits = response.data.logs[ix].split('!!!').map(x => x.trim())
            const key = splits[1]
            const value = splits[2]

            if (requests.some(request => request.key === key)) {
              const request = requests.find(r => r.key === key)
              this.log_pairs.push({
                key,
                request: JSON.parse(JSON.parse(request.value)),
                response: JSON.parse(JSON.parse(value)),
              });

              response.data.logs.splice(ix, 1);
              requests = requests.filter(x => x !== request)
              ix -= 1
            } else {
              requests.push({ key, value })
              response.data.logs.splice(ix, 1);
              ix -= 1
            }
          }
        }

        this.logs = response.data.logs
        this.dirty = false
      }).catch((error) => {
        console.error(error)
      }).finally(() => {
        this.loading = false
      })
    },
    rateColor(serviceName) {
      let backgroundColor = ""
      let color = ""

      if (serviceName.includes("FedEx")) {
        backgroundColor = "#51A917"
        color = "white"
      } else if (serviceName.includes("DHL")) {
        backgroundColor = "red"
        color = "white"
      } else if (serviceName.includes("USPS")) {
        backgroundColor = "orange"
        color = "white"
      } else {
        backgroundColor = "#A836FF"
        color = "white"
      }
      return ({backgroundColor, color, padding: "2px"})
    },
    loadBoxMode() {
      this.loading = true;
      this.boxes = [];
      this.packingMode = 1;

      this.shipments.forEach((shipment) => {
        this.boxes.push({
          length: Number(shipment.parcel.length),
          width: Number(shipment.parcel.width),
          height: Number(shipment.parcel.height),
          weight: Number(shipment.parcel.weight),
          value: Number(shipment.parcel.value),
        })
      })

      this.loading = false;
    },
    selectShipment(ix) {
      this.selectedShipmentIx = ix;
      this.$refs.shipmentInfoRef.show();
    },
    parcelVariant(shipment) {
      return shipment.parcel.items.some(i => !!i.special_method) ? 'text-warning' : 'text-dark';
    },
    niceContents(shipment) {
      const map = { };
      shipment.parcel.items.forEach((item) => {
        if (map[item.sku] === undefined) {
          map[item.sku] = 1
        } else {
          map[item.sku] += 1;
        }
      });
      return Object.entries(map).map(item => `${item[0]} x ${item[1]}`);
    },
  },
  mounted() {
    this.getCountries()
    if (this.$route.query.orderNumber) {
      this.orderId = this.$route.query.orderNumber;
      this.fetchOrder();
    }
  },
}
</script>

<style scoped>
.card-loader {
  position: absolute;
  width: 100%;
  height: 100%;
  background-color: rgba(128,128,128,0.75);
  z-index: 100000;
  top: 0;
  left: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  color: white;
}
pre {
  background-color: #222;
  color: #f8f4f0;
  font-size: 0.66rem;
  max-height: 33vh;
  overflow-y: scroll;
}
</style>



// WEBPACK FOOTER //
// src/components/pack-and-ship/PackAndShip2.vue